// GenerateVendorLinkDialog.jsx
// Dialog for property owners/managers to generate vendor links with requirements

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
} from "@mui/material";
import { Link as LinkIcon, ContentCopy, CheckCircle } from "@mui/icons-material";
import useMaintenanceVendorLinks from "../../hooks/useMaintenanceVendorLinks";

const GenerateVendorLinkDialog = ({ open, onClose, listingId, propertyAddress }) => {
  const [linkName, setLinkName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Checkbox states
  const [twentyFourHourNotice, setTwentyFourHourNotice] = useState(false);
  const [petsOnProperty, setPetsOnProperty] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const [emergencyType, setEmergencyType] = useState(""); 
  const [addDetails, setAddDetails] = useState(false);
  
  // Additional details form fields (shown when addDetails is checked)
  const [tenantFirstName, setTenantFirstName] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [tenantEmail, setTenantEmail] = useState("");
  const [lockLocation, setLockLocation] = useState("");

  const { generateVendorLink } = useMaintenanceVendorLinks();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setLinkName("");
      setError("");
      setSuccess(false);
      setGeneratedLink(null);
      setCopied(false);
      // Reset checkboxes
      setTwentyFourHourNotice(false);
      setPetsOnProperty(false);
      setEmergency(false);
      setEmergencyType("");
      setAddDetails(false);
      // Reset additional details
      setTenantFirstName("");
      setTenantPhone("");
      setTenantEmail("");
      setLockLocation("");
    }
  }, [open]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");

    try {
      const linkRequirements = {
        twentyFourHourNotice: twentyFourHourNotice,
        petsOnProperty: petsOnProperty,
        emergency: emergency,
        emergencyType: emergency ? (emergencyType.trim() || null) : null,
      };

      const tenantDetails = addDetails ? {
        tenantFirstName: tenantFirstName.trim() || null,
        tenantPhone: tenantPhone.trim() || null,
        tenantEmail: tenantEmail.trim() || null,
        lockLocation: lockLocation.trim() || null,
      } : null;

      const extraDetails = {
        linkRequirements: linkRequirements,
      };
      
      if (tenantDetails) {
        extraDetails.tenantDetails = tenantDetails;
      }

      // Log the data being sent to database
      console.log('[GENERATE VENDOR LINK] Data being sent to database:', {
        listingId,
        linkName: linkName || null,
        slots: [],
        linkRequirements: {
          twentyFourHourNotice,
          petsOnProperty,
          emergency,
          emergencyType: emergency ? emergencyType.trim() : null,
        },
        tenantDetails: tenantDetails || null,
        extraDetails: Object.keys(extraDetails).length > 0 ? extraDetails : null,
        timestamp: new Date().toISOString(),
      });

      // Generate link with empty slots array (vendors will select their own time)
      const result = await generateVendorLink(
        listingId,
        [], // Empty slots - vendors will select their own time
        linkName || undefined,
        Object.keys(extraDetails).length > 0 ? extraDetails : null
      );

      console.log('[GENERATE VENDOR LINK] Link generated successfully:', {
        linkId: result.linkId,
        linkUrl: result.linkUrl,
        timestamp: new Date().toISOString(),
      });

      setGeneratedLink(result.linkUrl);
      setSuccess(true);
    } catch (err) {
      console.error("[GENERATE VENDOR LINK] Error generating vendor link:", err);
      setError(err.message || "Failed to generate vendor link. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;

    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      setError("Failed to copy link to clipboard.");
    }
  };

  const handleClose = () => {
    if (success) {
      // Reset and close
      setSuccess(false);
      setGeneratedLink(null);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Generate Maintenance Vendor Link
      </DialogTitle>
      <DialogContent>
        {success && generatedLink ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Vendor link generated successfully!
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Share this link with your maintenance vendor. They will be able to select their own date and time for the visit.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                value={generatedLink}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                onClick={handleCopyLink}
                color={copied ? "success" : "primary"}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Link Name (Optional)"
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
              placeholder="e.g., HVAC Maintenance - January 2024"
              sx={{ mb: 3 }}
            />

            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              Link Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the requirements that apply to this maintenance vendor link.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={twentyFourHourNotice}
                    onChange={(e) => setTwentyFourHourNotice(e.target.checked)}
                  />
                }
                label="24 Hour Notice Minimum (Non-Emergency Tenant Occupied)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mb: 1 }}>
                Maintenance vendor must schedule at least 24 hours in advance.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={petsOnProperty}
                    onChange={(e) => setPetsOnProperty(e.target.checked)}
                  />
                }
                label="Pets on Property"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mb: 1 }}>
                Notify vendor to be aware of pets at the property.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={emergency}
                    onChange={(e) => {
                      setEmergency(e.target.checked);
                      if (!e.target.checked) {
                        setEmergencyType("");
                      }
                    }}
                  />
                }
                label="Emergency"
              />
              {emergency && (
                <TextField
                  fullWidth
                  value={emergencyType}
                  onChange={(e) => setEmergencyType(e.target.value)}
                  placeholder="What kind of emergency? (e.g., Water leak, HVAC failure)"
                  sx={{ ml: 4, mt: 1, mb: 1, maxWidth: 'calc(100% - 32px)' }}
                />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mb: 1 }}>
                If checked, maintenance vendors can schedule for whenever works for them.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={addDetails}
                    onChange={(e) => setAddDetails(e.target.checked)}
                  />
                }
                label="Add Details"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mb: 1 }}>
                Add tenant information and lock location details.
              </Typography>
            </Box>

            {/* Additional Details Form - shown when addDetails is checked */}
            {addDetails && (
              <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                  Tenant & Property Details (Optional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Tenant First Name"
                      value={tenantFirstName}
                      onChange={(e) => setTenantFirstName(e.target.value)}
                      placeholder="John"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Tenant Phone Number"
                      value={tenantPhone}
                      onChange={(e) => setTenantPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      type="tel"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Tenant Email Address"
                      value={tenantEmail}
                      onChange={(e) => setTenantEmail(e.target.value)}
                      placeholder="tenant@example.com"
                      type="email"
                      helperText="Used to send automatic notifications when maintenance is scheduled"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Lock Location at Property"
                      value={lockLocation}
                      onChange={(e) => setLockLocation(e.target.value)}
                      placeholder="e.g., Front door, Side entrance, Garage door"
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {success ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleGenerate}
              variant="contained"
              disabled={generating}
              startIcon={generating ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              {generating ? "Generating..." : "Generate Link"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateVendorLinkDialog;
